import React, { useEffect, useRef, useState } from 'react'

import { Icon, ArrowRight, Close } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { shape, string } from 'prop-types'

import { useGenerationStatus } from 'Pods/Shared/GenerationStatus'
import { clearGenerationResult, dismissGenerationResult } from 'Pods/Shared/GenerationStatus/actions'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

const STUDIO_MOUNT_ID = 'three-d-studio-elements'
const AUTO_DISMISS_MS = 6000
const SUCCEEDED = 'succeeded'
const ICON_SIZE = { width: '16', height: '16' }
const selectLastResult = (state) => state.lastResult
const selectUserId = (state) => state.userId
const selectDismissedRunId = (state) => state.dismissedRunId

// "Your 3D model is ready!" toast. Shown off /ai when a generation succeeds (the
// studio owns the on-page completion UI, so it is suppressed while /ai is mounted).
// It auto-dismisses after a few seconds and can be closed early. Auto-dismiss is a
// per-page guard (local state) that may re-show the same run on refresh; an explicit
// close (X or Open Model) persists to the store, so a closed run never reappears across
// reloads/tabs. A fresh run, with a new runId, always shows again. It rides the nav's own
// `new_top_navigation` gate - it only mounts when the new nav does - so it needs no
// separate flag of its own.
function GenerationToast({ labels = EMPTY_OBJECT, currentUserId = null }) {
  const storeLastResult = useGenerationStatus(selectLastResult)
  const ownerUserId = useGenerationStatus(selectUserId)
  const closedRunId = useGenerationStatus(selectDismissedRunId)
  const [autoDismissedRunId, setAutoDismissedRunId] = useState(null)
  // The store rehydrates from localStorage synchronously on the client, so a returning
  // user with a finished run would render the toast where the idle server emitted nothing.
  // Gate on a post-mount flag so the first client render matches SSR (null), then reveal.
  const [hydrated, setHydrated] = useState(false)
  const timerRef = useRef(null)

  // localStorage is shared across every user of this browser, so only surface a finished
  // run to the user who owns it - never a later anon visitor or a different account.
  // Both ids are normalized to strings (see resolveViewerId), and the compare stays
  // fail-closed: an unresolved viewer (null) withholds the run rather than leaking it.
  const ownedByViewer = currentUserId != null && ownerUserId != null
    && String(ownerUserId) === String(currentUserId)
  const lastResult = hydrated && ownedByViewer ? storeLastResult : null
  const runId = lastResult?.runId
  const isSuccess = lastResult?.status === SUCCEEDED
  const studioPresent = typeof document !== 'undefined' && !!document.getElementById(STUDIO_MOUNT_ID)
  const dismissed = runId === autoDismissedRunId || runId === closedRunId
  const visible = isSuccess && !studioPresent && runId != null && !dismissed

  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!visible) {
      return undefined
    }

    timerRef.current = setTimeout(() => setAutoDismissedRunId(runId), AUTO_DISMISS_MS)

    return () => clearTimeout(timerRef.current)
  }, [visible, runId])

  if (!visible) {
    return null
  }

  function handleDismiss() {
    dismissGenerationResult(runId)
  }

  function handleOpen() {
    clearGenerationResult()
  }

  return (
    <div className="generation-toast" role="status" aria-live="polite">
      <span className="generation-toast__preview">
        {lastResult.previewUrl && (
          <img className="generation-toast__image" src={lastResult.previewUrl} alt="" />
        )}
      </span>
      <div className="generation-toast__body">
        <p className="generation-toast__title cgt-heading-100">{labels.readyTitle}</p>
        <a className="generation-toast__cta" href={lastResult.pathname} onClick={handleOpen}>
          <span className="generation-toast__cta-text">{labels.openModel}</span>
          <Icon notSpaced size={ICON_SIZE} svgComponent={ArrowRight} className="generation-toast__cta-icon" />
        </a>
      </div>
      <button
        type="button"
        className="generation-toast__close"
        onClick={handleDismiss}
        aria-label={labels.dismiss}
      >
        <Icon notSpaced size={ICON_SIZE} svgComponent={Close} className="generation-toast__close-icon" />
      </button>
    </div>
  )
}

GenerationToast.propTypes = {
  labels: shape({
    readyTitle: string,
    openModel: string,
    dismiss: string,
  }),
  currentUserId: string,
}

export default GenerationToast
